<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import DomoEmbed from '@/components/DomoEmbed.vue'
import { useUserStore } from '@/authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { fetchDomoEmbeds, type DomoEmbed as DomoEmbedType } from '@/analytics/domo-api'
import ProgressSpinner from 'primevue/progressspinner'
import GbInlineMessage from '@/components/GbInlineMessage.vue'

const userStore = useUserStore()
const visibilityStore = useVisibilityStore()
const analytics = getAnalyticsTracker()

const availableDashboards = ref<DomoEmbedType[]>([])
const isLoading = ref<boolean>(true)
const error = ref<string | null>(null)

const selectedEmbed = ref<DomoEmbedType | null>(null)
const filters = ref<Record<string, string>>({})

const role = computed(() => (userStore.isSchoolAdmin ? 'admin' : 'user'))

const fetchAvailableDashboards = async () => {
  try {
    isLoading.value = true
    error.value = null
    
    const data = await fetchDomoEmbeds()
    availableDashboards.value = data || []
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load dashboards'
    console.error('Error fetching dashboards:', err)
  } finally {
    isLoading.value = false
  }
}

watch(selectedEmbed, (dashboard) => {
  if (!dashboard) return

  analytics.sendEvent('Viewed Analytics Dashboard', {
    dashboard_id: dashboard.embed_id,
    dashboard_name: dashboard.name,
    dashboard_ids: availableDashboards.value.map(d => d.embed_id),
    dashboard_names: availableDashboards.value.map(d => d.name),
    role: role.value,
  })
})

onMounted(async () => {
  await fetchAvailableDashboards()

  // Auto-select dashboard if user only has one available
  if (availableDashboards.value.length === 1) {
    selectedEmbed.value = availableDashboards.value[0]
  }
})
</script>

<template>
  <div class="px-6 pt-12" v-if="userStore.isSuperAdmin || userStore.isGoalbookBusinessUser || visibilityStore.analyticsDashboardVisibility">
    <div class="max-w-7xl mx-auto">
      <h1 class="text-center text-display-xl font-normal leading-[60px] mb-6">Analytics Dashboard</h1>
      <p class="text-center text-xl max-w-none">Insights into educator activation, engagement trends, and resource usage.</p>
      <div class="bg-white rounded-lg shadow-md px-6">
        <div v-if="isLoading" class="flex items-center justify-center min-h-[300px]">
          <div class="text-center">
            <ProgressSpinner class="mb-4 h-5 w-5 stroke-primary-400" strokeWidth="4" />
            <p class="text-gray-700">Loading available dashboards...</p>
          </div>
        </div>
        
        <!-- Error state -->
        <div v-else-if="error" class="flex items-center justify-center min-h-[200px]">
          <GbInlineMessage gbTheme="red" class="mb-4 w-3/5">
            <template #icon></template>
            <div class="inline-flex items-center">
              <span class="material-icons-round text-red-300 mr-2 self-start">warning_amber</span>
              <div class="space-y-1">
                <p class="font-bold text-base leading-6 ">{{ error }}</p>
                <p class="text-sm leading-5">We're having trouble loading the Analytics Dashboard. Please check your internet connection and refresh the page to try again.</p>
              </div>
            </div>
          </GbInlineMessage>
        </div>
        
        <!-- No dashboards available -->
        <div v-else-if="availableDashboards.length === 0" class="flex items-center justify-center min-h-[200px]">
          <p>No analytics dashboards are available for your account.</p>
        </div>
        
        <!-- Dashboard content -->
        <div v-else>
          <!-- Dashboard selection dropdown - show for users with multiple dashboards -->
          <div class="mb-6 py-6" v-if="availableDashboards.length > 1">
            <label for="dashboardSelect" class="block text-sm font-medium text-gray-700 mb-2">
              Select Dashboard
            </label>
            <select
              id="dashboardSelect"
              :value="selectedEmbed?.embed_id || ''"
              @change="selectedEmbed = availableDashboards.find(d => d.embed_id === ($event.target as HTMLSelectElement).value) || null"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">-- Select a dashboard --</option>
              <option
                v-for="dashboard in availableDashboards"
                :key="dashboard.embed_id"
                :value="dashboard.embed_id"
              >
                {{ dashboard.name }}
              </option>
            </select>
            <p class="mt-1 text-xs text-gray-700">
              Choose which analytics dashboard to display
            </p>
          </div>

          <DomoEmbed
            v-if="selectedEmbed"
            :embed-id="selectedEmbed.embed_id"
            :embed-name="selectedEmbed.name"
            :filters="filters"
          />
          
          <div v-else class="text-center py-12 text-gray-700">
            <p>Select a dashboard to view analytics</p>
            <p class="text-xs mt-2">Each dashboard provides different insights and metrics</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
